'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { getLenis } from '../hooks/useSmoothScroll'
import { track } from '../lib/analytics'

const HEX_DOT = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'

function HudCorners({ size = 16 }: { size?: number }) {
  const c = 'var(--color-fire)'
  const b = { position: 'absolute' as const, width: size, height: size, opacity: 0.6 }
  return (
    <>
      <span aria-hidden style={{ ...b, top: 0, left: 0, borderTop: `1px solid ${c}`, borderLeft: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, top: 0, right: 0, borderTop: `1px solid ${c}`, borderRight: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, bottom: 0, left: 0, borderBottom: `1px solid ${c}`, borderLeft: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, bottom: 0, right: 0, borderBottom: `1px solid ${c}`, borderRight: `1px solid ${c}` }} />
    </>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.6rem',
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'var(--color-stone)',
  marginBottom: 7,
}
const fieldStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-light)',
  padding: '0.7rem 0.85rem',
  fontSize: '0.92rem',
  fontFamily: 'var(--font-outfit), sans-serif',
  outline: 'none',
  borderRadius: 2,
  transition: 'border-color 0.25s, background 0.25s',
}

type Status = 'idle' | 'sending' | 'sent' | 'error'

export default function ContactModal({ open, onClose, armed = false }: { open: boolean; onClose: () => void; armed?: boolean }) {
  const [mounted, setMounted] = useState(false)
  const [reduced, setReduced] = useState(false)
  const [show, setShow] = useState(false)
  const [rendered, setRendered] = useState(false)
  const [bytes, setBytes] = useState(0) // live payload size readout on the message field
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const panelRef = useRef<HTMLDivElement>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setMounted(true)
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [])

  // Lock body scroll + Lenis while open; drive the enter/exit transition and
  // keep the node mounted through the exit animation, then restore focus.
  useEffect(() => {
    if (open) {
      setRendered(true)
      const prevFocus = document.activeElement as HTMLElement | null
      const lenis = getLenis()
      lenis?.stop()
      const prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      const raf = requestAnimationFrame(() => {
        setShow(true)
        firstFieldRef.current?.focus()
      })
      return () => {
        cancelAnimationFrame(raf)
        lenis?.start()
        document.body.style.overflow = prevOverflow
        prevFocus?.focus?.()
      }
    }
    // closing — play the exit transition, then unmount + reset
    setShow(false)
    const t = setTimeout(() => {
      setRendered(false)
      setStatus('idle')
      setErrorMsg('')
      setBytes(0)
    }, 360)
    return () => clearTimeout(t)
  }, [open])

  const close = useCallback(() => {
    if (status === 'sending') return
    onClose()
  }, [onClose, status])

  // Esc to close + basic focus trap.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return
      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      )
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, close])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (status === 'sending') return
    const form = e.currentTarget
    const data = {
      name: (form.elements.namedItem('name') as HTMLInputElement).value,
      email: (form.elements.namedItem('email') as HTMLInputElement).value,
      message: (form.elements.namedItem('message') as HTMLTextAreaElement).value,
      company: (form.elements.namedItem('company') as HTMLInputElement).value, // honeypot
    }
    setStatus('sending')
    setErrorMsg('')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      })
      const json = await res.json().catch(() => ({}))
      if (res.ok && json.ok) {
        setStatus('sent')
        track('contact', { method: 'form_submit' })
      } else {
        setStatus('error')
        setErrorMsg(json.error || 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setErrorMsg('Network error. Please check your connection and try again.')
    }
  }

  if (!mounted || (!open && !rendered)) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Contact form"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        display: 'grid',
        placeItems: 'center',
        padding: 'clamp(1rem, 4vw, 2.5rem)',
        background: 'rgba(5,5,5,0.82)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        opacity: show ? 1 : 0,
        transition: 'opacity 0.3s var(--ease-out-quart)',
      }}
    >
      <div
        ref={panelRef}
        /* Lenis is stopped while the modal is open, and a stopped Lenis
           preventDefault()s every wheel event — so this panel could never
           scroll on short viewports. Tells Lenis to let the event through. */
        data-lenis-prevent
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '34rem',
          maxHeight: '90dvh',
          overflowY: 'auto',
          background: 'var(--color-abyss)',
          border: '1px solid var(--color-border)',
          padding: 'clamp(1.5rem, 4vw, 2.5rem)',
          boxShadow: '0 40px 120px -40px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,107,0,0.06)',
          // armed = opened from the reticle: the panel scales out of the core center.
          // reduced-motion: no transform at all — the overlay opacity fade alone carries it.
          transformOrigin: 'center center',
          transform: reduced
            ? 'none'
            : show
              ? 'translateY(0) scale(1)'
              : armed
                ? 'scale(0.9)'
                : 'translateY(12px) scale(0.98)',
          transition: reduced ? 'none' : 'transform 0.35s var(--ease-out-quart)',
        }}
      >
        <HudCorners size={18} />

        {/* close */}
        <button
          type="button"
          onClick={close}
          aria-label="Close contact form"
          style={{
            position: 'absolute', top: 12, right: 12, width: 40, height: 40,
            display: 'grid', placeItems: 'center', color: 'var(--color-stone)',
            fontFamily: 'var(--font-jetbrains), monospace', fontSize: '0.85rem',
            border: '1px solid var(--color-border)', background: 'transparent',
            cursor: 'pointer', transition: 'color 0.25s, border-color 0.25s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-fire)'; e.currentTarget.style.borderColor = 'var(--color-fire)' }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-stone)'; e.currentTarget.style.borderColor = 'var(--color-border)' }}
        >
          ✕
        </button>

        {/* header */}
        <div className="font-mono" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span aria-hidden style={{ width: 9, height: 10, clipPath: HEX_DOT, background: 'var(--color-fire)', boxShadow: '0 0 9px var(--color-fire)', display: 'inline-block' }} />
          <span style={{ fontSize: '0.62rem', letterSpacing: '0.26em', textTransform: 'uppercase', color: 'var(--color-fog)' }}>Establish Uplink</span>
        </div>
        <p className="font-mono" style={{ fontSize: '0.58rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--color-stone)', marginBottom: 'clamp(1.25rem, 3vh, 1.75rem)' }}>
          // transmit a message · I reply within 24h
        </p>

        {status === 'sent' ? (
          <div role="status" style={{ padding: '1.5rem 0', textAlign: 'center' }}>
            {/* Coordinates lock — the transmission landed at the uplink core */}
            <div aria-hidden style={{ fontSize: '2rem', color: 'var(--color-fire)', marginBottom: 10, textShadow: '0 0 22px rgba(255,107,0,0.5)' }}>◉</div>
            <p className="font-mono uppercase" style={{ fontSize: '0.72rem', letterSpacing: '0.3em', color: 'var(--color-fire)', marginBottom: 10 }}>Signal received</p>
            <p className="font-mono uppercase" style={{ fontSize: '0.58rem', letterSpacing: '0.2em', color: 'var(--color-stone)', marginBottom: 14 }}>Cairo &middot; 30.0444&deg; N, 31.2357&deg; E</p>
            <p className="font-mono" style={{ fontSize: '0.7rem', letterSpacing: '0.1em', color: 'var(--color-fog)', marginBottom: 22 }}>Thanks — I&rsquo;ll get back to you soon.</p>
            <button
              type="button"
              onClick={close}
              className="font-mono uppercase"
              style={{ fontSize: '0.65rem', letterSpacing: '0.22em', color: 'var(--color-fire)', border: '1px solid rgba(255,107,0,0.4)', padding: '0.7rem 1.6rem', background: 'transparent', cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* honeypot — visually hidden, off-screen, not announced */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <label htmlFor="company">Company (leave blank)</label>
              <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <div style={{ marginBottom: '1.1rem' }}>
              <label htmlFor="cf-name" style={labelStyle}><span style={{ color: 'var(--color-fire)' }}>F1 //</span> Name</label>
              <input
                ref={firstFieldRef}
                id="cf-name" name="name" type="text" required maxLength={100} autoComplete="name"
                placeholder="Your name"
                style={fieldStyle}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-fire)' }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
              />
            </div>

            <div style={{ marginBottom: '1.1rem' }}>
              <label htmlFor="cf-email" style={labelStyle}><span style={{ color: 'var(--color-fire)' }}>F2 //</span> Email</label>
              <input
                id="cf-email" name="email" type="email" required maxLength={200} autoComplete="email"
                placeholder="you@company.com"
                style={fieldStyle}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-fire)' }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
              />
            </div>

            <div style={{ marginBottom: '1.35rem' }}>
              <div className="flex items-center justify-between">
                <label htmlFor="cf-message" style={labelStyle}><span style={{ color: 'var(--color-fire)' }}>F3 //</span> Message</label>
                <span className="font-mono" aria-hidden style={{ fontSize: '0.55rem', letterSpacing: '0.14em', color: bytes > 4700 ? 'var(--color-ember)' : 'var(--color-stone)' }}>
                  {bytes} / 5000 BYTES
                </span>
              </div>
              <textarea
                id="cf-message" name="message" required minLength={10} maxLength={5000} rows={5}
                placeholder="Tell me about your project, role, or idea…"
                style={{ ...fieldStyle, resize: 'vertical', minHeight: 120 }}
                onChange={(e) => setBytes(e.currentTarget.value.length)}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-fire)' }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
              />
            </div>

            {status === 'error' && (
              <p role="alert" className="font-mono" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--color-ember)', marginBottom: '1rem' }}>
                ▸ {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="font-display font-bold"
              style={{
                width: '100%',
                fontSize: '0.95rem',
                letterSpacing: '0.04em',
                color: 'var(--color-on-fire, #050505)',
                background: 'var(--color-fire)',
                padding: '0.95rem 1.5rem',
                border: 'none',
                cursor: status === 'sending' ? 'wait' : 'pointer',
                opacity: status === 'sending' ? 0.7 : 1,
                transition: 'opacity 0.25s, filter 0.25s',
              }}
              onMouseEnter={(e) => { if (status !== 'sending') e.currentTarget.style.filter = 'brightness(1.1)' }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = 'none' }}
            >
              {status === 'sending' ? 'TRANSMITTING…' : '◉ TRANSMIT ▸'}
            </button>

            <p className="font-mono" style={{ fontSize: '0.55rem', letterSpacing: '0.12em', color: 'var(--color-stone)', marginTop: 14, textAlign: 'center' }}>
              Or email directly · abdelrahman.18036@gmail.com
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body,
  )
}
